import API_BASE from '../config';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';

export default function Webhooks() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [client, setClient] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deliveriesFor, setDeliveriesFor] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [showSecret, setShowSecret] = useState(null);

  // form state for create/edit
  const [formUrl, setFormUrl] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formEvents, setFormEvents] = useState([]);
  const [formActive, setFormActive] = useState(true);
  const [saving, setSaving] = useState(false);

  async function authFetch(url, opts = {}) {
    const token = await getToken();
    return fetch(url, {
      ...opts,
      headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
    });
  }

  async function loadClient() {
    try {
      const res = await authFetch(API_BASE + '/clients/' + id);
      if (res.ok) setClient(await res.json());
    } catch (_) {}
  }
  async function loadWebhooks() {
    setLoading(true);
    try {
      const res = await authFetch(API_BASE + '/clients/' + id + '/webhooks');
      if (!res.ok) throw new Error('Failed to load webhooks');
      setWebhooks(await res.json());
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }
  async function loadEventTypes() {
    try {
      const res = await authFetch(API_BASE + '/webhooks/event-types');
      if (res.ok) {
        const data = await res.json();
        setEventTypes(Array.isArray(data) ? data : (data.event_types || []));
      }
    } catch (_) {}
  }
  async function loadDeliveries(wid) {
    try {
      const res = await authFetch(API_BASE + '/webhooks/' + wid + '/deliveries');
      if (res.ok) setDeliveries(await res.json());
    } catch (e) { toast.error('Failed to load deliveries'); }
  }

  useEffect(() => { loadClient(); loadWebhooks(); loadEventTypes(); }, [id]);

  function openCreate() {
    setFormUrl(''); setFormDescription(''); setFormEvents([]); setFormActive(true);
    setEditing(null); setCreateOpen(true);
  }
  function openEdit(wh) {
    setFormUrl(wh.url || ''); setFormDescription(wh.description || '');
    setFormEvents(Array.isArray(wh.event_types) ? wh.event_types : []);
    setFormActive(wh.active !== false);
    setEditing(wh); setCreateOpen(true);
  }
  function toggleEvent(ev) {
    setFormEvents(prev => prev.includes(ev) ? prev.filter(e => e !== ev) : [...prev, ev]);
  }
  async function saveWebhook() {
    if (!formUrl.trim()) { toast.error('URL required'); return; }
    if (formEvents.length === 0) { toast.error('Pick at least one event'); return; }
    setSaving(true);
    try {
      const body = { url: formUrl.trim(), description: formDescription.trim(), event_types: formEvents, active: formActive };
      let res;
      if (editing) {
        res = await authFetch(API_BASE + '/webhooks/' + editing.id, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        res = await authFetch(API_BASE + '/clients/' + id + '/webhooks', { method: 'POST', body: JSON.stringify(body) });
      }
      if (!res.ok) throw new Error(await res.text());
      const saved = await res.json();
      toast.success(editing ? 'Webhook updated' : 'Webhook created');
      if (!editing && saved.secret) setShowSecret({ id: saved.id, secret: saved.secret });
      setCreateOpen(false);
      await loadWebhooks();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    setSaving(false);
  }
  async function deleteWebhook(wid) {
    if (!window.confirm('Delete this webhook? Deliveries history will be removed.')) return;
    try {
      const res = await authFetch(API_BASE + '/webhooks/' + wid, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Deleted');
      await loadWebhooks();
    } catch (e) { toast.error(e.message); }
  }
  async function testWebhook(wid) {
    try {
      const res = await authFetch(API_BASE + '/webhooks/' + wid + '/test', { method: 'POST', body: JSON.stringify({}) });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Test event dispatched');
    } catch (e) { toast.error('Test failed: ' + e.message); }
  }
  async function regenSecret(wid) {
    if (!window.confirm('Regenerate secret? You will need to update your receiver.')) return;
    try {
      const res = await authFetch(API_BASE + '/webhooks/' + wid + '/regenerate-secret', { method: 'POST', body: JSON.stringify({}) });
      if (!res.ok) throw new Error('Regenerate failed');
      const data = await res.json();
      setShowSecret({ id: wid, secret: data.secret });
      toast.success('Secret regenerated');
      await loadWebhooks();
    } catch (e) { toast.error(e.message); }
  }
  async function enableWebhook(wid) {
    try {
      const res = await authFetch(API_BASE + '/webhooks/' + wid + '/enable', { method: 'POST', body: JSON.stringify({}) });
      if (!res.ok) throw new Error('Enable failed');
      toast.success('Webhook enabled');
      await loadWebhooks();
    } catch (e) { toast.error(e.message); }
  }
  async function replayDelivery(did) {
    try {
      const res = await authFetch(API_BASE + '/deliveries/' + did + '/replay', { method: 'POST', body: JSON.stringify({}) });
      if (!res.ok) throw new Error('Replay failed');
      toast.success('Replayed');
      if (deliveriesFor) await loadDeliveries(deliveriesFor.id);
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ padding: '1.5rem 2rem', maxWidth: '1100px', margin: '0 auto' }}>
        <button onClick={() => navigate('/clients/' + id)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>
          {client ? '← ' + client.name : '← Back'}
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 500, margin: '0 0 0.25rem', color: '#1A1A1A' }}>Webhooks</h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>
              Send events to Zapier, Make, n8n, or your own endpoint. <a href="/docs/webhooks" style={{ color: '#8B1A1A' }}>Docs →</a>
            </p>
          </div>
          <button onClick={openCreate} style={{ fontSize: '0.85rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', cursor: 'pointer', fontWeight: 500 }}>+ New webhook</button>
        </div>

        {loading ? (
          <p style={{ color: '#888' }}>Loading…</p>
        ) : webhooks.length === 0 ? (
          <div style={{ background: '#fff', border: '0.5px dashed #D4CFC9', borderRadius: '10px', padding: '2rem', textAlign: 'center', color: '#888' }}>
            No webhooks yet. Click <strong>+ New webhook</strong> to create one.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {webhooks.map(wh => (
              <div key={wh.id} style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1rem 1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                      <span style={{ fontSize: '0.7rem', color: wh.disabled_at ? '#C53030' : (wh.active ? '#276749' : '#888'), background: wh.disabled_at ? '#FED7D7' : (wh.active ? '#C6F6D5' : '#EDF2F7'), padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {wh.disabled_at ? 'Auto-disabled' : (wh.active ? 'Active' : 'Inactive')}
                      </span>
                      {wh.description && <span style={{ fontSize: '0.85rem', color: '#1A1A1A', fontWeight: 500 }}>{wh.description}</span>}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#1A1A1A', fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{wh.url}</div>
                    <div style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.35rem' }}>
                      Events: {(wh.event_types || []).join(', ') || '—'}
                    </div>
                    {wh.disabled_at && (
                      <div style={{ fontSize: '0.72rem', color: '#C53030', marginTop: '0.35rem' }}>
                        Disabled: {wh.disabled_reason || 'consecutive failures'}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', justifyContent: 'flex-end' }}>
                    <button onClick={() => testWebhook(wh.id)} style={btnSecondary}>Test</button>
                    <button onClick={() => { setDeliveriesFor(wh); loadDeliveries(wh.id); }} style={btnSecondary}>Deliveries</button>
                    <button onClick={() => openEdit(wh)} style={btnSecondary}>Edit</button>
                    <button onClick={() => regenSecret(wh.id)} style={btnSecondary}>Regen secret</button>
                    {wh.disabled_at && <button onClick={() => enableWebhook(wh.id)} style={{ ...btnSecondary, color: '#276749', borderColor: '#9AE6B4' }}>Re-enable</button>}
                    <button onClick={() => deleteWebhook(wh.id)} style={{ ...btnSecondary, color: '#C53030', borderColor: '#FED7D7' }}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {createOpen && (
        <div style={modalBackdrop} onClick={() => !saving && setCreateOpen(false)}>
          <div style={modalBody} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>{editing ? 'Edit webhook' : 'New webhook'}</h2>
            <label style={lbl}>URL</label>
            <input type="text" value={formUrl} onChange={e => setFormUrl(e.target.value)} placeholder="https://hooks.zapier.com/hooks/catch/…" style={inp} />
            <label style={lbl}>Description (optional)</label>
            <input type="text" value={formDescription} onChange={e => setFormDescription(e.target.value)} placeholder="Send new prospects to Zapier" style={inp} />
            <label style={lbl}>Active</label>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={formActive} onChange={e => setFormActive(e.target.checked)} /> Send events to this URL
              </label>
            </div>
            <label style={lbl}>Events</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '1rem', maxHeight: '240px', overflowY: 'auto', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '0.65rem' }}>
              {eventTypes.map(ev => (
                <label key={ev} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={formEvents.includes(ev)} onChange={() => toggleEvent(ev)} />
                  <span style={{ fontFamily: 'monospace' }}>{ev}</span>
                </label>
              ))}
              {eventTypes.length === 0 && <span style={{ fontSize: '0.8rem', color: '#888' }}>No events available</span>}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setCreateOpen(false)} disabled={saving} style={btnSecondary}>Cancel</button>
              <button onClick={saveWebhook} disabled={saving} style={{ fontSize: '0.85rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', cursor: 'pointer', fontWeight: 500 }}>{saving ? 'Saving…' : (editing ? 'Save' : 'Create')}</button>
            </div>
          </div>
        </div>
      )}

      {showSecret && (
        <div style={modalBackdrop} onClick={() => setShowSecret(null)}>
          <div style={modalBody} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem' }}>Signing secret</h2>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#888' }}>Store this somewhere safe. You won't see it again.</p>
            <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', background: '#F5F5F0', padding: '0.75rem', borderRadius: '6px', overflowWrap: 'anywhere', marginBottom: '1rem' }}>{showSecret.secret}</div>
            <button onClick={() => { navigator.clipboard.writeText(showSecret.secret); toast.success('Copied'); }} style={btnSecondary}>Copy</button>
            <button onClick={() => setShowSecret(null)} style={{ ...btnSecondary, marginLeft: '0.5rem' }}>Close</button>
          </div>
        </div>
      )}

      {deliveriesFor && (
        <div style={modalBackdrop} onClick={() => setDeliveriesFor(null)}>
          <div style={{ ...modalBody, maxWidth: '720px' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem' }}>Deliveries · {deliveriesFor.description || deliveriesFor.url}</h2>
            <div style={{ maxHeight: '480px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {deliveries.length === 0 && <span style={{ color: '#888', fontSize: '0.85rem' }}>No deliveries yet</span>}
              {deliveries.map(d => (
                <div key={d.id} style={{ border: '0.5px solid #E8E4E0', borderRadius: '6px', padding: '0.6rem 0.75rem', fontSize: '0.78rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                    <span style={{ fontFamily: 'monospace' }}>{d.event_type}</span>
                    <span style={{ color: d.status === 'success' ? '#276749' : (d.status === 'failed' ? '#C53030' : '#B7791F') }}>{d.status} · {d.response_status || '—'}</span>
                  </div>
                  <div style={{ color: '#888', fontSize: '0.72rem', marginTop: '0.2rem' }}>
                    Attempt {d.attempt}/{d.max_attempts} · {d.created_at ? new Date(d.created_at).toLocaleString() : ''}
                  </div>
                  {d.error_message && <div style={{ color: '#C53030', fontSize: '0.72rem', marginTop: '0.2rem' }}>{d.error_message}</div>}
                  <div style={{ marginTop: '0.35rem' }}>
                    <button onClick={() => replayDelivery(d.id)} style={{ ...btnSecondary, fontSize: '0.72rem', padding: '0.25rem 0.6rem' }}>Replay</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button onClick={() => setDeliveriesFor(null)} style={btnSecondary}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btnSecondary = { fontSize: '0.78rem', color: '#1A1A1A', background: '#fff', border: '0.5px solid #D4CFC9', borderRadius: '6px', padding: '0.35rem 0.7rem', cursor: 'pointer' };
const lbl = { display: 'block', fontSize: '0.7rem', color: '#888', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 };
const inp = { width: '100%', fontSize: '0.85rem', border: '0.5px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.65rem', marginBottom: '0.75rem', boxSizing: 'border-box', outline: 'none' };
const modalBackdrop = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalBody = { background: '#fff', borderRadius: '12px', padding: '1.5rem', maxWidth: '520px', width: '90%', maxHeight: '90vh', overflowY: 'auto' };
